import React, { useEffect, useRef, useState } from 'react'
import { Button, Modal, Tabs, Upload } from 'antd';
import styled from 'styled-components';
import { UploadOutlined } from '@ant-design/icons';
import { UploadChangeParam } from 'antd/es/upload';
import { showErrorToast } from '../../Utlis/Toast';
import { fetchFolderFileNameAPI } from '../../../../Redux/ApiCalls/Dashboard/FolderApi';
import LoadingDots from '../../Utlis/LoadinDots';

const { TabPane } = Tabs;

interface UploadImageModalComponentPropsType {
    handleImageUpload: (files: File[]) => void;
    closeModal: () => void;
    folder_id: string
}

const UploadImageModalComponent: React.FC<UploadImageModalComponentPropsType> = ({ closeModal, handleImageUpload, folder_id }) => {
    const [uploadedFiles, setUploadedFiles] = useState<Set<string>>(new Set());

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [folderFiles, setFolderFiles] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [reuploadModalData, setReuploadModalData] = useState<{ visible: boolean, files: File[] }>({ visible: false, files: [] });
    const [finalImages, setFinalImages] = useState<File[]>([])

    useEffect(() => {
        if (folder_id) {
            setLoading(true);
            fetchFolderFileNameAPI(folder_id).then(res => {
                const alreadyUploadedFileNames = res.file_names;
                if (alreadyUploadedFileNames && alreadyUploadedFileNames.length) {
                    setFolderFiles(alreadyUploadedFileNames);
                }
            }).catch((err) => {
                console.log("error in fetching file name", err)
            }).finally(() => {
                setLoading(false)
            })
        }
    }, [])

    // useEffect(() => {
    //     if (isConfirmUpload) {
    //         handleImageUpload(finalImages)
    //     }
    // }, [isConfirmUpload])



    const handleModalImageUpload = async (info: UploadChangeParam<any>) => {
        const files = info.fileList.map((file) => file.originFileObj).filter(Boolean) as File[];

        if (!files || files.length === 0) return;

        // Filter unique files
        const uniqueFiles = files.filter(file => {
            if (uploadedFiles.has(file.name)) {
                return false; // Skip file if it's already uploaded
            } else {
                uploadedFiles.add(file.name); // Add to set if it's new
                return true; // Include file in unique files
            }
        });
        const duplicate = uniqueFiles.filter(file => folderFiles.includes(file.name))
        if (uniqueFiles.length) {

            if (duplicate.length > 0) {
                setFinalImages(uniqueFiles.filter(file => !folderFiles.includes(file.name)))
                setReuploadModalData({ visible: true, files: duplicate })
            } else {
                handleImageUpload(uniqueFiles)
            }

        }
    };
    const handleFolderSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || []);

        if (files.length === 0 && files.length > 1000) {
            showErrorToast("Too many files detected.");
            return;
        };

        // Filter out non-image files
        const imageFiles = files.filter((file) => {
            return file.type.startsWith('image/');
        });

        // Filter out duplicate files
        const uniqueFiles = imageFiles.filter((file) => {
            if (uploadedFiles.has(file.name)) {
                return false; // Skip if already selected
            } else {
                uploadedFiles.add(file.name); // Mark file as uploaded
                return true;
            }
        });
        // Pass unique files back to parent component
        const duplicate = uniqueFiles.filter(file => folderFiles.includes(file.name))
        if (uniqueFiles.length) {

            if (duplicate.length > 0) {
                setFinalImages(uniqueFiles.filter(file => !folderFiles.includes(file.name)))
                setReuploadModalData({ visible: true, files: duplicate })
            } else {
                handleImageUpload(uniqueFiles)
            }

        }

        if (fileInputRef.current) {
            fileInputRef.current.value = ''; // Reset the input to clear the selected files
        };

    };

 const renameFile = (file: File): File => {
    const dotIndex = file.name.lastIndexOf('.');
    const base = dotIndex !== -1 ? file.name.slice(0, dotIndex) : file.name;
    const ext = dotIndex !== -1 ? file.name.slice(dotIndex) : '';
    const uniqueSuffix = `_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
    return new File([file], `${base}${uniqueSuffix}${ext}`, {
        type: file.type,
        lastModified: file.lastModified,
    });
};

const onConfirmDuplicate = () => {
    const renamedDuplicates = reuploadModalData.files.map(renameFile);
    handleImageUpload([...finalImages, ...renamedDuplicates]);
    setReuploadModalData({ visible: false, files: [] }); // reset so stale data doesn't linger
    closeModal();
};

const onDisCardDuplicate = () => {
    handleImageUpload(finalImages);
    setReuploadModalData({ visible: false, files: [] }); // reset here too
    closeModal();
};
    return (
        <Container>
            {loading ? <LoadingDots /> : <StyledTabs defaultActiveKey="1">
                {/* Tab 1: Upload Image */}
                <TabPane tab="Upload Image" key="1">
                    <TabContent>
                        <Upload accept="image/*" showUploadList={false} multiple onChange={handleModalImageUpload} beforeUpload={() => false}>
                            <StyledUploadButton icon={<UploadOutlined />}>Click to Upload Image</StyledUploadButton>
                        </Upload>
                    </TabContent>
                </TabPane>

                {/* Tab 2: Upload Folder */}
                <TabPane tab="Upload Folder" key="2">
                    <TabContent>
                        <StyledUploadButton icon={<UploadOutlined />} onClick={() => { fileInputRef.current?.click() }}>Click to Upload Folder</StyledUploadButton>
                        <input
                            ref={fileInputRef} // Attach the ref to the input
                            type="file"
                            // ts-ignore
                            {...({ webkitdirectory: 'true' } as any)}
                            multiple
                            style={{ display: "none" }}
                            onChange={handleFolderSelection}
                        />
                    </TabContent>
                </TabPane>
            </StyledTabs>}

            <Modal
                open={reuploadModalData.visible}
                onCancel={onDisCardDuplicate}
                footer={null}
                centered
                width={480}
            >
                <ModalHeader>Upload again?</ModalHeader>
                <ModalSubtext>
                    These files already exist in this folder. Continue to upload
                    again or discard to keep the current versions.
                </ModalSubtext>
                <DuplicateList>
                    {reuploadModalData.files.map((file) => (
                        <li key={file.name}>{file.name}</li>
                    ))}
                </DuplicateList>
                <ModalActions>
                    <ActionButton $variant="ghost" onClick={onConfirmDuplicate}>
                        Continue
                    </ActionButton>
                    <ActionButton onClick={onDisCardDuplicate}>
                        Discard Duplicates
                    </ActionButton>
                </ModalActions>
            </Modal>
        </Container>
    )
}

export default UploadImageModalComponent


const Container = styled.div``;


// Styled Tabs component
const StyledTabs = styled(Tabs)`
  .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: #AE2AB1 !important; /* Set active tab color (e.g., blue) */
  }

  .ant-tabs-ink-bar {
    background-color: #AE2AB1 !important; /* Customize the tab indicator color */
  }
    .ant-tabs-tab:hover .ant-tabs-tab-btn {
    color: #AE2AB1 !important; /* Hover color (e.g., green) */
  }
`;

// Styled Tab content wrapper
const TabContent = styled.div`
  text-align: center;
  padding: 20px;
`;

// Styled Upload Button
const StyledUploadButton = styled(Button)`
  margin-top: 20px;
  transition: all 0.3s ease;

  &:hover {
    color: #AE2AB1 !important;
    border-color: #AE2AB1 !important; 
    box-shadow: 0 0 10px rgba(174, 42, 177, 0.5);
  }
`;

const ModalHeader = styled.h3`
  margin-bottom: 8px;
  color: #1f1f1f;
`;

const ModalSubtext = styled.p`
  color: #6b6b6b;
  margin-bottom: 16px;
`;

const DuplicateList = styled.ul`
  max-height: 180px;
  overflow-y: auto;
  padding-left: 18px;
  margin-bottom: 24px;
  li {
    margin-bottom: 4px;
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const ActionButton = styled.button <{ $variant?: 'ghost' }>`
  ${({ $variant }) =>
        $variant === 'ghost'
            ? `
    background-color: transparent;
    color: #8c8c8c;
    border: 1px solid #8c8c8c;
    cursor:pointer;
  `
            : `
    cursor:pointer;
   background: linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%);
    border: none;
    color:white ;
  `}
  padding: 4px 8px 4px 8px;
  border-radius:10px;
  
`;
